import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { getInterestsForIntents, normalizeOtherInterest } from "@/lib/onboardingData";
import { useAuth } from "@/context/AuthContext";
import {
    OnboardingShell,
    OptionCard,
    GradientPrimaryButton,
} from "@/screens/onboarding/_ui";

const MIN_INTERESTS = 1;
const RECOMMENDED = 3;

const OnboardingInterests = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();

    // Per-user onboarding key — never bleed across accounts
    const STORAGE_KEY = `neara.onboarding.${user?.id || "anon"}`;

    const readProgress = () => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        } catch {
            return null;
        }
    };
    const writeProgress = (patch) => {
        const cur = readProgress() || {};
        const next = { ...cur, ...patch };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
    };

    // Require Step 1 to have been completed
    const saved = readProgress();
    useEffect(() => {
        if (!saved || !saved.intents || saved.intents.length === 0) {
            navigate("/onboarding", { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dynamicInterests = useMemo(
        () => getInterestsForIntents(saved?.intents || []),
        [saved?.intents?.join("|")]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Important: do NOT prefill from a previous user's selections
    const [selected, setSelected] = useState(saved?.interests || []);
    const [othersSelected, setOthersSelected] = useState(!!saved?.interestOther);
    const [otherText, setOtherText] = useState(saved?.interestOther || "");

    const effectiveCount =
        selected.length + (othersSelected && otherText.trim().length > 0 ? 1 : 0);
    const canFinish = effectiveCount >= MIN_INTERESTS;
    const remainingToRecommended = Math.max(0, RECOMMENDED - effectiveCount);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            writeProgress({ interests: next });
            return next;
        });
    };

    const toggleOthers = () => {
        setOthersSelected((prev) => {
            const next = !prev;
            if (!next) {
                setOtherText("");
                writeProgress({ interestOther: null });
            }
            return next;
        });
    };

    const handleOtherChange = (v) => {
        setOtherText(v);
        writeProgress({ interestOther: v.trim() || null });
    };

    const handleFinish = () => {
        if (!canFinish) return;
        const finalInterests = [...selected];
        const otherRaw = othersSelected ? otherText.trim() : "";
        const normalizedOther = otherRaw ? normalizeOtherInterest(otherRaw) : null;
        // If "Other" maps to a known bucket (e.g. dating → social), fold into list
        if (normalizedOther && !normalizedOther.custom && !finalInterests.includes(normalizedOther.id)) {
            finalInterests.push(normalizedOther.id);
        }
        writeProgress({
            interests: finalInterests,
            interestOther: normalizedOther?.custom ? normalizedOther.label : null,
            completed: true,
        });
        updateUser({
            interests: finalInterests,
            interestOther: normalizedOther?.custom ? normalizedOther.label : null,
            onboardingCompleted: true,
        });
        navigate("/home", { replace: true });
    };

    return (
        <OnboardingShell
            testId="onboarding-interests-screen"
            step={2}
            total={2}
            title="Pick your interests"
            subtitle={`Pick at least ${MIN_INTERESTS} interest. Choose ${RECOMMENDED} or more for better recommendations.`}
            footer={
                <GradientPrimaryButton
                    testId="onboarding-interests-finish"
                    onClick={handleFinish}
                    disabled={!canFinish}
                >
                    {!canFinish
                        ? `Pick ${MIN_INTERESTS - effectiveCount} to continue`
                        : remainingToRecommended > 0
                        ? `Finish (${remainingToRecommended} more for better picks)`
                        : "Finish"}
                </GradientPrimaryButton>
            }
        >
            <div
                className="mt-1 flex items-center justify-between text-[12px] text-muted-foreground px-1 pt-3"
            >
                <span>Selected</span>
                <span
                    className="font-medium text-primary"
                    data-testid="onboarding-interests-count"
                >
                    {effectiveCount} · {RECOMMENDED}+ recommended
                </span>
            </div>

            <div
                className="grid grid-cols-3 gap-2.5 pt-3"
                data-testid="onboarding-interests-grid"
            >
                {dynamicInterests.map((i) => (
                    <OptionCard
                        key={i.id}
                        testId={`interest-${i.id}`}
                        icon={i.icon}
                        label={i.label}
                        selected={selected.includes(i.id)}
                        onClick={() => toggle(i.id)}
                        variant="tile"
                    />
                ))}
                <OptionCard
                    testId="interest-others"
                    icon={Sparkles}
                    label="Others"
                    selected={othersSelected}
                    onClick={toggleOthers}
                    variant="tile"
                />
            </div>

            {othersSelected && (
                <div className="mt-5 animate-slide-up" data-testid="onboarding-interest-other-wrap">
                    <label className="block">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Add your own interest
                        </span>
                        <span className="flex items-center h-14 px-4 rounded-2xl border border-border bg-secondary focus-within:border-primary focus-within:bg-background transition-colors">
                            <input
                                data-testid="onboarding-interest-other-input"
                                value={otherText}
                                onChange={(e) => handleOtherChange(e.target.value)}
                                placeholder="e.g. chess, gardening, K-drama"
                                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70"
                                maxLength={40}
                                autoFocus
                            />
                        </span>
                        <span className="block text-[11.5px] text-muted-foreground mt-1.5 ml-1">
                            Counts as 1 interest when filled.
                        </span>
                    </label>
                </div>
            )}

            <div className="h-6" />
        </OnboardingShell>
    );
};

export default OnboardingInterests;
